import { useState } from "react";
import { Bell, Send, AlertTriangle, Users, Store, Globe } from "lucide-react";
import apiClient from "../../lib/api-client";
import { useGlobalToast } from "../../contexts/ToastContext";
import { useConfirm } from "../../contexts/ConfirmContext";

type Audience = "endusers" | "staff" | "all";

const AUDIENCES: {
  value: Audience;
  label: string;
  description: string;
  icon: typeof Users;
}[] = [
  {
    value: "endusers",
    label: "Clientes",
    description: "Usuarios con la app de cliente instalada",
    icon: Users,
  },
  {
    value: "staff",
    label: "Personal",
    description: "Equipo de los negocios con la app",
    icon: Store,
  },
  {
    value: "all",
    label: "Todos",
    description: "Clientes y personal",
    icon: Globe,
  },
];

const TITLE_MAX = 100;
const BODY_MAX = 240;

export default function PushBroadcast() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<Audience>("endusers");
  const [sending, setSending] = useState(false);

  const canSend = title.trim().length > 0 && body.trim().length > 0 && !sending;

  const handleSend = async () => {
    if (!canSend) return;

    const audienceLabel =
      AUDIENCES.find((a) => a.value === audience)?.label ?? audience;
    const confirmed = await confirm({
      title: "Enviar notificación push",
      message: `Se enviará a «${audienceLabel}»: "${title.trim()}" — ${body.trim()}. Esta acción no se puede deshacer.`,
      confirmText: "Enviar",
      cancelText: "Cancelar",
    });
    if (!confirmed) return;

    setSending(true);
    try {
      const { data } = await apiClient.post("/admin/push-broadcast", {
        title: title.trim(),
        body: body.trim(),
        audience,
      });
      showToast(
        data?.message ?? "Notificación enviada correctamente",
        "success",
      );
      setTitle("");
      setBody("");
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Error al enviar la notificación";
      showToast(message, "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center">
          <Bell className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            Notificaciones push
          </h1>
          <p className="text-sm text-gray-500">
            Envía un aviso a las personas que tienen la app instalada
          </p>
        </div>
      </div>

      {/* Aviso legal */}
      <div className="flex gap-3 p-4 mb-6 rounded-xl bg-amber-50 border border-amber-200">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          Usa este canal solo para avisos legítimos. Las notificaciones{" "}
          <strong>promocionales</strong> requieren consentimiento de marketing
          del destinatario (RGPD). Para mensajes meramente informativos no
          promocionales no aplica esa restricción.
        </p>
      </div>

      {/* Formulario */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
        {/* Audiencia */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Destinatarios
          </label>
          <div className="grid grid-cols-3 gap-3">
            {AUDIENCES.map((a) => {
              const Icon = a.icon;
              const active = audience === a.value;
              return (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => setAudience(a.value)}
                  className={`flex flex-col items-center text-center gap-1 p-3 rounded-xl border transition ${
                    active
                      ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${active ? "text-indigo-600" : "text-gray-400"}`}
                  />
                  <span className="text-sm font-medium text-gray-900">
                    {a.label}
                  </span>
                  <span className="text-[11px] leading-tight text-gray-500">
                    {a.description}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Título */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Título
          </label>
          <input
            type="text"
            value={title}
            maxLength={TITLE_MAX}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej: ¡Nuevas funciones disponibles!"
            className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">
            {title.length}/{TITLE_MAX}
          </p>
        </div>

        {/* Mensaje */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Mensaje
          </label>
          <textarea
            value={body}
            maxLength={BODY_MAX}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            placeholder="Escribe el contenido de la notificación..."
            className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">
            {body.length}/{BODY_MAX}
          </p>
        </div>

        {/* Previsualización */}
        {(title.trim() || body.trim()) && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Previsualización
            </label>
            <div className="flex gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
              <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {title.trim() || "Título"}
                </p>
                <p className="text-sm text-gray-600">
                  {body.trim() || "Mensaje"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Enviar */}
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <Send className="w-4 h-4" />
          {sending ? "Enviando..." : "Enviar notificación"}
        </button>
      </div>
    </div>
  );
}
